'use client';

import React from 'react';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';

import ArrowForward from '@src/components/Icons/ArrowForward';

import Container from '@src/components/Shared/Container';
import InlineSVG from '@src/components/Shared/InlineSVG';
import ValdHubSlider from '@src/components/Shared/ValdHubSlider';
import NextImage from '@src/components/Shared/NextImage';
import SectionHeader from '@src/components/Shared/SectionHeader';
import RenderBodyCopy from '@src/components/Shared/RenderBodyCopy';
import RenderButtons from '@src/components/Shared/RenderButtons';
import NextLink from '@src/components/Shared/NextLink';

import ImageTextSecondary from '@src/components/Sections/ImageTextSecondary';
import SvgTextPrimary from '@src/components/Sections/SvgTextPrimary';
import CallToAction from '@src/components/Sections/CallToAction';

import usePreviewContent from '@src/hooks/usePreviewContent';

import isEmpty from '@src/utils/isEmpty';

import constructURL from '@src/utils/constructURL';

import { ValdHubPageClientPropTypes } from './ValdHubPage.types';
import Testimonials from '@src/components/Sections/Testimonials';

const ValdHubPageClient: React.FC<ValdHubPageClientPropTypes> = ({ data }) => {
  // Enable live updates for the post
  const page = usePreviewContent({ content: data?.page });

  // Set the page view path for analytics tracking
  const { setPathPageView, setContentType } = useTrackingCode();
  setPathPageView(
    constructURL({ languageCode: page?.languageCode?.toLowerCase(), slug: `/products/valdhub`, baseURL: '' }),
  );
  setContentType('site-page');

  return (
    <>
      <div
        id={page?.hero?.items?.[0]?.scrollTo?.slice(1)}
        className='valdHubHero bg-[radial-gradient(53.61%_117.15%_at_50%_50%,#2D2D2D_0%,#181818_74.11%)] py-[8rem]'
      >
        <Container className='pb-[8rem] md:pb-[17rem] xl:pb-[20rem]'>
          <InlineSVG
            className='mx-auto w-full max-w-[45.6rem]'
            src={page?.hero?.items?.[0]?.mediaPrimary?.url}
            renderAt={['desktop', 'tablet', 'mobile']}
          />
          <ValdHubSlider
            title={page?.hero?.items?.[0]?.title}
            preTitle={page?.hero?.items?.[0]?.preTitle}
            bodyCopy={page?.hero?.items?.[0]?.bodyCopy}
          />
        </Container>
      </div>

      <Container className='mb-[0]!' background='bg-background-tertiary'>
        <NextImage
          className='top-[-17rem] mx-auto w-[70%]! xl:top-[-30rem]'
          objectFit='cover'
          priority
          src={page?.hero?.items?.[0]?.mediaSecondary?.url}
          alt={page?.hero?.items?.[0]?.mediaSecondary?.description ?? 'VALD Image'}
          aspectRatio={page?.hero?.items?.[0]?.mediaSecondary?.width / page?.hero?.items?.[0]?.mediaSecondary?.height}
          width={page?.hero?.items?.[0]?.mediaSecondary?.width}
        />
        <div className='mx-auto mt-[-12rem] flex justify-center xl:mt-[-25rem]'>
          <ul className='mb-[6rem] flex w-full flex-wrap justify-around gap-[1rem] gap-y-[3rem] text-center md:w-[60%] xl:flex-nowrap'>
            {page?.hero?.items?.[0]?.extraAssetsCollection?.items?.map((asset) => {
              if (isEmpty({ value: asset })) return null;
              return (
                <li key={`${asset?.sys?.id}-svg-icons`} className='flex w-[21%] flex-col items-center justify-start'>
                  <InlineSVG
                    className='flex min-h-[3rem] items-center [&>svg]:h-[2.4rem] [&>svg]:w-[2.4rem]'
                    src={asset?.media?.url}
                    renderAt={['desktop', 'tablet', 'mobile']}
                  />
                  <p className='mt-[1.6rem] text-[1.6rem] leading-[1.7rem] font-medium'>{asset?.title}</p>
                </li>
              );
            })}
          </ul>
        </div>
      </Container>

      <Container
        id={page?.centralized?.items?.[0]?.scrollTo?.slice(1)}
        className='py-[5rem] md:flex md:flex-col'
        background='bg-background-tertiary'
      >
        <SectionHeader
          className='justify-center md:mb-[3.4rem] lg:pb-[unset]'
          pretitle={page?.centralized?.items?.[0]?.preTitle}
          title={page?.centralized?.items?.[0]?.title}
          titleClassName='max-w-[61.6rem] text-[2.8rem] lg:text-[4.8rem] mb-[2.4rem] leading-[3.2rem] lg:leading-[5rem] font-light'
          align='center'
        >
          <>
            <RenderBodyCopy
              className='lg:max-w-[78.2rem]'
              bodyCopy={page?.centralized?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons className='justify-center' items={page?.centralized?.items?.[0]?.buttonCollection?.items} />
          </>
        </SectionHeader>
      </Container>

      <ImageTextSecondary
        id={page?.fullFeatured?.items?.[0]?.scrollTo?.slice(1)}
        pretitle={page?.fullFeatured?.items?.[0]?.preTitle}
        title={page?.fullFeatured?.items?.[0]?.title}
        titleClassName='2xl:max-w-[66rem]'
        imgURL={page?.fullFeatured?.items?.[0]?.mediaPrimary?.url}
        objectFit='contain'
        childrenElements={
          <>
            <RenderBodyCopy
              className='2xl:max-w-[78.2rem]'
              bodyCopy={page?.fullFeatured?.items?.[0]?.bodyCopy?.json?.content}
            />

            <RenderButtons items={page?.fullFeatured?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <Container
        id={page?.customizable?.items?.[0]?.scrollTo?.slice(1)}
        className={`flex flex-col items-stretch md:gap-[2rem] lg:min-h-[65rem] lg:flex-row lg:items-center 2xl:mb-0!`}
      >
        {/* Mobile image */}
        <NextImage
          className='-order-1 mb-[3.2rem] lg:hidden'
          src={page?.customizable?.items?.[0]?.mediaPrimary?.url}
          alt={page?.customizable?.items?.[0]?.mediaPrimary?.description ?? 'VALD Image'}
          width={
            page?.customizable?.items?.[0]?.mediaPrimary?.width / page?.customizable?.items?.[0]?.mediaPrimary?.height
          }
          aspectRatio={page?.customizable?.items?.[0]?.mediaPrimary?.width}
          objectFit='contain'
        />

        {/* Tablet image */}
        <NextImage
          className='order-1 mr-[-2.8rem] hidden lg:block xl:mr-[-12.8rem] 2xl:hidden'
          src={page?.customizable?.items?.[0]?.mediaSecondary?.url}
          alt={page?.customizable?.items?.[0]?.mediaSecondary?.description ?? 'VALD Image'}
          width={
            page?.customizable?.items?.[0]?.mediaSecondary?.width /
            page?.customizable?.items?.[0]?.mediaSecondary?.height
          }
          aspectRatio={page?.customizable?.items?.[0]?.mediaSecondary?.width}
          objectFit='contain'
        />

        {/* Desktop image */}
        <NextImage
          className='order-1 mt-[4rem] mr-[-2.8rem] hidden w-[39.5rem] md:w-[65.8rem] lg:w-[81.8rem] xl:mt-0 xl:mr-[-12.8rem] 2xl:block'
          src={page?.customizable?.items?.[0]?.mediaTertiary?.url}
          alt={page?.customizable?.items?.[0]?.mediaTertiary?.description ?? 'VALD Image'}
          width={
            page?.customizable?.items?.[0]?.mediaTertiary?.width / page?.customizable?.items?.[0]?.mediaTertiary?.height
          }
          aspectRatio={page?.customizable?.items?.[0]?.mediaTertiary?.width}
          objectFit='contain'
        />

        <SectionHeader
          className='lg:w-8/12 lg:justify-start 2xl:w-7/12'
          titleClassName='text-[2.8rem] lg:text-[4.8rem] leading-[3.2rem] lg:leading-[5rem] font-light 2xl:max-w-[58.8rem] mb-[2.4rem]'
          pretitle={page?.customizable?.items?.[0]?.preTitle}
          title={page?.customizable?.items?.[0]?.title}
          align='left'
        >
          {
            <>
              <RenderBodyCopy
                className='2xl:max-w-[78.2rem]'
                bodyCopy={page?.customizable?.items?.[0]?.bodyCopy?.json?.content}
              />
              <RenderButtons items={page?.customizable?.items?.[0]?.buttonCollection?.items} />
            </>
          }
        </SectionHeader>
      </Container>

      <Container id={page?.normativeData?.items?.[0]?.scrollTo?.slice(1)} className='px-[0]'>
        <SectionHeader
          className='mb-[3.2rem] md:px-[6.4rem] lg:mb-[6.4rem]'
          preTitleClassName='mb-[1.6rem] w-full md:text-center'
          title={page?.normativeData?.items?.[0]?.title}
          pretitle={page?.normativeData?.items?.[0]?.preTitle}
          titleClassName='w-full md:text-center mb-[2.4rem] text-[2.2rem] leading-[2.6rem] tracking-[-0.0418] font-light text-[1.8rem] md:leading-[3rem] md:text-[2.8rem] lg:max-w-[90rem] 2xl:max-w-[118rem] lg:text-[4.8rem] lg:leading-[5rem]'
          childrenClassName='relative'
          align='center'
        >
          <RenderBodyCopy
            className='text-left md:mx-auto md:max-w-[60.7rem] md:text-center 2xl:max-w-[78.2rem]'
            bodyCopy={page?.normativeData?.items?.[0]?.bodyCopy?.json?.content}
          />
          <RenderButtons
            className='md:justify-center'
            items={page?.normativeData?.items?.[0]?.buttonCollection?.items}
          />
        </SectionHeader>

        <NextImage
          className='-order-1 mb-[3.2rem]'
          src={page?.normativeData?.items?.[0]?.mediaPrimary?.url}
          alt={page?.normativeData?.items?.[0]?.mediaPrimary?.description ?? 'VALD Image'}
          width={
            page?.normativeData?.items?.[0]?.mediaPrimary?.width / page?.normativeData?.items?.[0]?.mediaPrimary?.height
          }
          aspectRatio={page?.normativeData?.items?.[0]?.mediaPrimary?.width}
          objectFit='contain'
        />

        {/* TODO: this svg file doens't show 1 image inside it, temporarily use PNG */}
        {/* <InlineSVG className='' src={page?.normativeData?.items?.[0]?.mediaPrimary?.url} /> */}
      </Container>

      <ImageTextSecondary
        reversed
        id={page?.administrativeControls?.items?.[0]?.scrollTo?.slice(1)}
        className='overflow-visible! py-[8rem]'
        background='bg-background-primary'
        objectFit='contain'
        pretitle={page?.administrativeControls?.items?.[0]?.preTitle}
        title={page?.administrativeControls?.items?.[0]?.title}
        titleClassName='text-body-copy--light'
        imgURL={page?.administrativeControls?.items?.[0]?.mediaPrimary?.url}
        childrenElements={
          <>
            <RenderBodyCopy
              className='text-body-copy--light 2xl:max-w-[58.2rem]'
              bodyCopy={page?.administrativeControls?.items?.[0]?.bodyCopy?.json?.content}
            />
          </>
        }
      />

      <Testimonials
        id={page?.testimonials?.items?.[0]?.scrollTo?.slice(1)}
        testimonials={page?.testimonials?.items?.[0]?.topicCollection?.items}
        bgWhite={true}
      />

      <Container id={page?.integrations?.items?.[0]?.scrollTo?.slice(1)}>
        <SectionHeader
          className='mb-[3.2rem] md:px-[6.4rem]'
          preTitleClassName='mb-[1.6rem]'
          pretitle={page?.integrations?.items?.[0]?.preTitle}
          title={page?.integrations?.items?.[0]?.title}
          titleClassName='mb-[2.4rem] text-[2.2rem] leading-[2.6rem] tracking-[-0.0418] font-light text-[1.8rem] md:leading-[3rem] md:text-[2.8rem] lg:max-w-[60rem] 2xl:max-w-7xl lg:text-[4.8rem]  lg:leading-[5rem] '
          childrenClassName='relative'
          align='center'
        >
          <RenderBodyCopy
            className='mx-auto max-w-[51.7rem] text-left md:text-center 2xl:max-w-[78.2rem]'
            bodyCopy={page?.integrations?.items?.[0]?.bodyCopy?.json?.content}
          />
          <RenderButtons
            className='md:justify-center'
            items={page?.integrations?.items?.[0]?.buttonCollection?.items}
          />
        </SectionHeader>

        <NextImage
          className='mx-auto max-w-[100rem]'
          src={page?.integrations?.items?.[0]?.mediaPrimary?.url}
          alt={page?.integrations?.items?.[0]?.mediaPrimary?.description ?? 'VALD Image'}
          aspectRatio={
            page?.integrations?.items?.[0]?.mediaPrimary?.width / page?.integrations?.items?.[0]?.mediaPrimary?.height
          }
          width={page?.integrations?.items?.[0]?.mediaPrimary?.width} // Set actual width
        />
      </Container>

      <Container
        id={page?.blogList?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-background-tertiary'
        className='3xl:py-[10rem] flex flex-col items-center gap-[2.4rem] py-[8rem] md:gap-[unset] lg:flex-row'
      >
        <SectionHeader
          className='justify-center md:mb-[3.4rem] lg:w-[40%] lg:pb-[unset]'
          pretitle={page?.blogList?.items?.[0]?.preTitle}
          title={page?.blogList?.items?.[0]?.title}
          titleClassName='max-w-[61.6rem] text-[2.8rem] xl:text-[4.8rem] mb-[2.4rem] leading-[3.2rem] md:leading-[4.6rem] xl:leading-[5rem] font-light'
          align='left'
        >
          <>
            <RenderBodyCopy
              className='2xl:max-w-[48.2rem]'
              bodyCopy={page?.blogList?.items?.[0]?.bodyCopy?.json?.content}
            />

            <RenderButtons items={page?.blogList?.items?.[0]?.buttonCollection?.items} />
          </>
        </SectionHeader>
        <ul className='grid w-full grid-cols-1 gap-[2.4rem] md:flex lg:w-[60%]'>
          {page?.blogList?.items?.[0]?.extraAssetsCollection?.items?.map((post) => {
            if (isEmpty({ value: post })) return null;

            return (
              <li key={`${post?.sys?.id}-integrations`} className='w-full'>
                <NextLink className='rounded-2xl' href={post?.url}>
                  <NextImage
                    className='relative w-full'
                    src={post?.media?.url}
                    alt={post?.media?.description}
                    aspectRatio={post?.media?.width / post?.media?.height}
                    width={post?.media?.width}
                    objectFit='contain'
                  >
                    <div className='bg-background-primary text-body-copy--light absolute right-[1rem] bottom-[1.3rem] order-3 mt-[0rem]! flex items-center justify-center! space-y-[1rem] sm:space-y-[unset] [&>div>a]:w-full'>
                      <span className='mr-[0.8rem] font-normal'>{post?.title}</span>
                      <ArrowForward className='common-btn-transition hover:text-primary--v2 mt-[0.2rem]! h-[1.2rem] w-[0.646rem]' />
                    </div>
                  </NextImage>
                </NextLink>
              </li>
            );
          })}
        </ul>
      </Container>

      <Container className='w-full'>
        <NextImage
          className='hidden sm:hidden md:block lg:block xl:block'
          entryId={page?.securityPrivacyPolicy?.items?.[0]?.sys?.id}
          src={page?.securityPrivacyPolicy?.items?.[0]?.mediaPrimary?.url}
          alt={page?.securityPrivacyPolicy?.items?.[0]?.mediaPrimary?.description}
          aspectRatio={
            page?.securityPrivacyPolicy?.items?.[0]?.mediaPrimary?.width /
            page?.securityPrivacyPolicy?.items?.[0]?.mediaPrimary?.height
          }
          width={page?.securityPrivacyPolicy?.items?.[0]?.mediaPrimary?.width}
        />
        <NextImage
          className='block sm:block md:hidden lg:hidden xl:hidden'
          entryId={page?.securityPrivacyPolicy?.items?.[0]?.sys?.id}
          src={page?.securityPrivacyPolicy?.items?.[0]?.mediaSecondary?.url}
          alt={page?.securityPrivacyPolicy?.items?.[0]?.mediaSecondary?.description}
          aspectRatio={
            page?.securityPrivacyPolicy?.items?.[0]?.mediaSecondary?.width /
            page?.securityPrivacyPolicy?.items?.[0]?.mediaSecondary?.height
          }
          width={page?.securityPrivacyPolicy?.items?.[0]?.mediaSecondary?.width}
        />
      </Container>
      <ImageTextSecondary
        className='overflow-visible! lg:mb-[0]! lg:py-[0]'
        objectFit='contain'
        id={page?.reports?.items?.[0]?.scrollTo?.slice(1)}
        pretitle={page?.reports?.items?.[0]?.preTitle}
        title={page?.reports?.items?.[0]?.title}
        imgURL={page?.reports?.items?.[0]?.mediaPrimary?.url}
        imgSecondClassName='lg:w-[70%]! lg:bottom-[-1rem]'
        childrenElements={
          <>
            <RenderBodyCopy
              className='2xl:max-w-[78.2rem]'
              bodyCopy={page?.reports?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons className='justify-start' items={page?.reports?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <SvgTextPrimary
        reversed
        id={page?.systems?.items?.[0]?.scrollTo?.slice(1)}
        className='overflow-visible! py-[8rem]'
        background='bg-background-primary'
        pretitle={page?.systems?.items?.[0]?.preTitle}
        title={page?.systems?.items?.[0]?.title}
        titleClassName='text-body-copy--light'
        svgURL={page?.systems?.items?.[0]?.mediaPrimary?.url}
        svgClassName='flex items-center'
        childrenElements={
          <>
            <RenderBodyCopy
              className='text-body-copy--light 2xl:max-w-[58.2rem]'
              bodyCopy={page?.systems?.items?.[0]?.bodyCopy?.json?.content}
            />
          </>
        }
      />

      <CallToAction
        attr='call-to-action-component'
        id={page?.callToAction?.items?.[0]?.scrollTo?.slice(1)}
        title={page?.callToAction?.items?.[0]?.title}
        bodyCopy={page?.callToAction?.items?.[0]?.bodyCopy?.json?.content}
        buttons={page?.callToAction?.items?.[0]?.buttonCollection?.items}
      />
    </>
  );
};

export default ValdHubPageClient;
